import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Search, Send, Upload, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AdminPanel from "@/components/admin/AdminPanel";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { request, uploadFile } from "@/lib/api";

const PAGE_SIZE = 50;
// "Leave this column out" — Select can't hold an empty string as a value, so
// unmapped fields use this sentinel and it's stripped before the request.
const UNMAPPED = "__none__";

export default function EmployeeManager({ client, token, open = true, onClose, onClientChanged, inline = false }) {
  const [tab, setTab] = useState("people");
  const [roster, setRoster] = useState({ total: 0, employees: [] });
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Upload wizard: null -> file chosen (mapping) -> validated -> committed
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});
  const fileInput = useRef(null);

  // SMS delivery check — mobile sign-in fails with a deliberately vague
  // message to employees, so ops needs somewhere to see the real reason.
  const [smsCheck, setSmsCheck] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);

  const clientId = client?.client_id;

  const loadEmployees = useCallback(async () => {
    if (!clientId) return;
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (search.trim()) params.set("search", search.trim());
    try {
      setRoster(await request(`/admin/clients/${clientId}/employees?${params}`, {}, token));
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [clientId, offset, search, token]);

  useEffect(() => {
    if (!open) return;
    loadEmployees();
  }, [open, loadEmployees]);

  // Start each visit from a clean slate — a half-finished mapping from last
  // time is confusing and, worse, could be committed against a different org.
  useEffect(() => {
    if (!open) return;
    setTab("people");
    setUpload(null);
    setMapping({});
    setError("");
    setOffset(0);
    setSearch("");
  }, [open, clientId]);

  async function chooseFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      const result = await uploadFile(`/admin/clients/${clientId}/roster/upload`, file, token);
      setUpload(result);
      setMapping(result.suggested_mapping || {});
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setBusy(false);
      // Let the same file be picked again after a failure.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function runValidate() {
    setError("");
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, column]) => column && column !== UNMAPPED));
      setUpload(await request(`/admin/roster/${upload.upload_id}/validate`, { method: "POST", body: JSON.stringify({ mapping: clean }) }, token));
    } catch (validateError) {
      setError(validateError.message);
    } finally {
      setBusy(false);
    }
  }

  async function runCommit() {
    setError("");
    setBusy(true);
    try {
      setUpload(await request(`/admin/roster/${upload.upload_id}/commit`, { method: "POST" }, token));
      await loadEmployees();
    } catch (commitError) {
      setError(commitError.message);
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (upload && upload.status !== "committed") {
      try {
        await request(`/admin/roster/${upload.upload_id}`, { method: "DELETE" }, token);
      } catch {
        // A discard that fails leaves an uncommitted staging row behind and
        // changes nothing the user can see — not worth an error banner.
      }
    }
    setUpload(null);
    setMapping({});
    setError("");
  }

  async function toggleEmployee(employee, key, value) {
    setError("");
    try {
      await request(`/admin/employees/${encodeURIComponent(employee.email)}`, { method: "PUT", body: JSON.stringify({ [key]: value }) }, token);
      await loadEmployees();
    } catch (updateError) {
      setError(updateError.message);
    }
  }

  async function checkSms() {
    setSmsBusy(true);
    setError("");
    try {
      // No mobile number: probes with an unroutable destination, so this
      // verifies the credentials without messaging a real person.
      setSmsCheck(await request("/admin/diagnostics/sms", { method: "POST", body: JSON.stringify({}) }, token));
    } catch (checkError) {
      setError(checkError.message);
    } finally {
      setSmsBusy(false);
    }
  }

  async function toggleAllowlist(enabled) {
    setError("");
    try {
      await request(`/admin/clients/${clientId}/roster-policy`, { method: "PUT", body: JSON.stringify({ roster_is_allowlist: enabled }) }, token);
      await onClientChanged?.();
    } catch (policyError) {
      setError(policyError.message);
    }
  }

  const fields = upload?.fields || [];
  const columns = upload?.detected_columns || [];
  const totalPages = Math.max(1, Math.ceil(roster.total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <AdminPanel inline={inline} open={open} onClose={onClose} title={`Employees — ${client?.name || ""}`} testId="admin-employee-manager">

        {error && <p className="form-error" role="alert" data-testid="employee-manager-error">{error}</p>}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="people" data-testid="employee-tab-people">People ({roster.total})</TabsTrigger>
            <TabsTrigger value="upload" data-testid="employee-tab-upload">Bulk upload</TabsTrigger>
            <TabsTrigger value="access" data-testid="employee-tab-access">Sign-in rules</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "people" && (
          <div className="employee-people">
            <div className="employee-toolbar">
              <div className="employee-search">
                <Search size={15} />
                <Input
                  placeholder="Search name, email or mobile"
                  value={search}
                  onChange={(event) => { setSearch(event.target.value); setOffset(0); }}
                  data-testid="employee-search-input"
                />
              </div>
              <Button variant="outline" onClick={() => setTab("upload")} data-testid="employee-goto-upload"><Upload size={15} />Upload list</Button>
            </div>
            <Table data-testid="employee-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Mobile</TableHead>
                  <TableHead>ID</TableHead><TableHead>Added</TableHead><TableHead>Eligible</TableHead><TableHead>HR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roster.employees.map((employee) => (
                  <TableRow key={employee.email} data-testid={`employee-row-${employee.email}`}>
                    <TableCell>{employee.name}</TableCell>
                    <TableCell className="employee-email">{employee.email}</TableCell>
                    <TableCell>
                      {employee.mobile || <span className="employee-muted">Not set — no mobile sign-in</span>}
                    </TableCell>
                    <TableCell>{employee.employee_code || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={employee.source === "roster" ? "default" : "secondary"}>
                        {employee.source === "roster" ? "Roster" : "Self sign-up"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={employee.is_eligible}
                        onCheckedChange={(value) => toggleEmployee(employee, "is_eligible", value)}
                        data-testid={`employee-eligible-${employee.email}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={employee.is_hr}
                        onCheckedChange={(value) => toggleEmployee(employee, "is_hr", value)}
                        data-testid={`employee-hr-${employee.email}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!roster.employees.length && (
                  <TableRow><TableCell colSpan={7} className="employee-muted">No employees yet. Upload this organisation's list to get started.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            {roster.total > PAGE_SIZE && (
              <div className="employee-pager">
                <Button variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
                <span>Page {currentPage} of {totalPages}</span>
                <Button variant="outline" disabled={currentPage >= totalPages} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
              </div>
            )}
          </div>
        )}

        {tab === "upload" && (
          <div className="employee-upload">
            {/* Step 1 — pick a file */}
            {!upload && (
              <div className="upload-dropzone" data-testid="roster-dropzone">
                <FileSpreadsheet size={28} />
                <h4>Upload the employee list</h4>
                <p>A .csv or .xlsx file with one row per employee. Any column order — you'll map the columns in the next step.</p>
                <p className="employee-muted">
                  Allowed email domains for {client?.name}: {(client?.allowed_email_domains || []).join(", ") || "none configured"}
                </p>
                <input ref={fileInput} type="file" accept=".csv,.xlsx,.xlsm" onChange={chooseFile} hidden data-testid="roster-file-input" />
                <Button onClick={() => fileInput.current?.click()} disabled={busy} data-testid="roster-choose-file">
                  <Upload size={15} />{busy ? "Reading file…" : "Choose file"}
                </Button>
              </div>
            )}

            {/* Step 2 — map columns */}
            {upload && upload.status === "pending_mapping" && (
              <div className="upload-mapping" data-testid="roster-mapping">
                <div className="upload-summary">
                  <strong>{upload.filename}</strong>
                  <span>{upload.total_rows} rows · {columns.length} columns</span>
                  <Button variant="ghost" size="sm" onClick={discard} data-testid="roster-discard"><X size={14} />Start over</Button>
                </div>
                <p className="employee-muted">We matched your columns to ours automatically. Correct anything we got wrong.</p>
                <Table>
                  <TableHeader><TableRow><TableHead>Our field</TableHead><TableHead>Your column</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {fields.map((field) => (
                      <TableRow key={field.key}>
                        <TableCell>
                          {field.label}{field.required && <span className="upload-required"> *</span>}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={mapping[field.key] || UNMAPPED}
                            onValueChange={(value) => setMapping((current) => ({ ...current, [field.key]: value === UNMAPPED ? "" : value }))}
                          >
                            <SelectTrigger className="w-[240px]" data-testid={`roster-map-${field.key}`}>
                              <SelectValue placeholder="Not in my file" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={UNMAPPED}>Not in my file</SelectItem>
                              {columns.map((column) => <SelectItem key={column} value={column}>{column}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="employee-muted">{field.help}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="upload-actions">
                  <Button onClick={runValidate} disabled={busy || !mapping.email} data-testid="roster-validate">
                    {busy ? "Checking…" : "Check the data"}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3 — validation result */}
            {upload && upload.status === "validated" && (
              <div className="upload-review" data-testid="roster-review">
                <div className="upload-stats">
                  <div className="upload-stat upload-stat--ok"><CheckCircle2 size={18} /><strong>{upload.valid_rows}</strong><span>ready to import</span></div>
                  <div className={`upload-stat ${upload.error_rows ? "upload-stat--bad" : ""}`}><AlertTriangle size={18} /><strong>{upload.error_rows}</strong><span>will be skipped</span></div>
                  <div className="upload-stat"><strong>{upload.total_rows}</strong><span>rows in file</span></div>
                </div>
                {upload.error_rows > 0 && (
                  <>
                    <p className="employee-muted">
                      These rows won't be imported. Fix them in your file and upload again, or continue and import just the {upload.valid_rows} valid rows.
                    </p>
                    <Table data-testid="roster-errors">
                      <TableHeader><TableRow><TableHead>Row</TableHead><TableHead>What's wrong</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {(upload.errors || []).map((row) => (
                          <TableRow key={row.row_number}>
                            <TableCell>{row.row_number}</TableCell>
                            <TableCell>
                              <ul className="upload-error-list">{(row.errors || []).map((message) => <li key={message}>{message}</li>)}</ul>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {upload.error_rows > (upload.errors || []).length && (
                      <p className="employee-muted">Showing the first {(upload.errors || []).length} of {upload.error_rows} problem rows.</p>
                    )}
                  </>
                )}
                <div className="upload-actions">
                  <Button variant="outline" onClick={() => setUpload({ ...upload, status: "pending_mapping" })} data-testid="roster-back-to-mapping">Change mapping</Button>
                  <Button variant="ghost" onClick={discard}>Start over</Button>
                  <Button onClick={runCommit} disabled={busy || !upload.valid_rows} data-testid="roster-commit">
                    {busy ? "Importing…" : `Import ${upload.valid_rows} employees`}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4 — done */}
            {upload && upload.status === "committed" && (
              <div className="upload-done" data-testid="roster-done">
                <CheckCircle2 size={32} />
                <h4>Import complete</h4>
                <p>{upload.created_count} added · {upload.updated_count} updated · {upload.error_rows} skipped</p>
                <div className="upload-actions">
                  <Button variant="outline" onClick={() => { setUpload(null); setMapping({}); setTab("people"); }} data-testid="roster-view-people">View employees</Button>
                  <Button onClick={() => { setUpload(null); setMapping({}); }}>Upload another file</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "access" && (
          <div className="employee-access" data-testid="employee-access">
            <div className="access-row">
              <div>
                <strong>Only people on the uploaded list can sign in</strong>
                <p className="employee-muted">
                  On: an employee must already be on this organisation's list to receive a sign-in code. Off: anyone with an email on
                  {" "}{(client?.allowed_email_domains || []).join(", ") || "an allowed domain"} can sign in and is added automatically.
                </p>
                <p className="employee-muted">
                  Upload the full employee list before turning this on — anyone missing from it will be locked out.
                </p>
              </div>
              <Switch
                checked={Boolean(client?.roster_is_allowlist)}
                onCheckedChange={toggleAllowlist}
                data-testid="employee-allowlist-toggle"
              />
            </div>
            <div className="access-row access-row--stacked">
              <div>
                <strong>Mobile sign-in</strong>
                <p className="employee-muted">
                  Employees can sign in with their mobile number instead of email, but only if that number is on their record here.
                  {" "}{roster.employees.filter((employee) => employee.mobile).length} of the {roster.employees.length} people on this page have one.
                </p>
                <p className="employee-muted">
                  If employees see “We could not send your verification code”, check delivery here — it reports what the SMS provider
                  actually said. The check sends nothing to a real number.
                </p>
                <Button variant="outline" onClick={checkSms} disabled={smsBusy} data-testid="sms-check-button">
                  <Send size={15} />{smsBusy ? "Checking…" : "Check SMS delivery"}
                </Button>

                {smsCheck && (
                  <div className={smsCheck.ok ? "sms-check sms-check--ok" : "sms-check sms-check--bad"} data-testid="sms-check-result">
                    <strong>
                      {smsCheck.ok
                        ? "SMS delivery is working."
                        : `SMS delivery is failing${smsCheck.code ? ` — provider said ${smsCheck.code}` : ""}.`}
                    </strong>
                    <span>{smsCheck.detail}</span>
                    <small className="employee-muted">
                      mode {smsCheck.delivery_mode} · sending {smsCheck.notifications_enabled ? "enabled" : "disabled"}
                      {smsCheck.sender_id ? ` · sender ${smsCheck.sender_id}` : ""}
                      {smsCheck.template_ok ? "" : " · template has no variable slot, codes would never be inserted"}
                    </small>
                    {!smsCheck.ok && smsCheck.code === "1703" && (
                      <small className="employee-muted">
                        The provider is rejecting the credentials. Confirm the API username and password (these differ from the
                        portal login), and whether the account requires the calling IP to be whitelisted.
                      </small>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
    </AdminPanel>
  );
}
